import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Switch,
  CircularProgress,
  Alert,
  Autocomplete,
} from "@mui/material";
import { registerUser } from "../../services/user.service";
import { fetchPersonnel } from "../../services/personnel/personnel.service";
import "./UserModals.css";
import { AppButton } from "components/common/buttons";

const AddUserModal = ({ open, onClose, onUserAdded }) => {
  const [formData, setFormData] = useState({
    userName: "",
    password: "",
    confirmPassword: "",
    fullName: "",
    email: "",
    personnelId: null,
    isActive: false,
  });

  const [personnelOptions, setPersonnelOptions] = useState([]);
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!open) return;

    let isMounted = true;
    fetchPersonnel(1000, 1, null, "FullName", "ASC", true).then((response) => {
      if (isMounted) {
        setPersonnelOptions(
          (response.items || []).map((item) => ({
            ...item,
            personnelId: item.personnelId ?? item.PersonnelId,
            personnelCode: item.personnelCode ?? item.PersonnelCode,
            fullName: item.fullName ?? item.FullName ?? item.personnelName ?? item.PersonnelName,
            email: item.email ?? item.Email,
          }))
        );
      }
    });

    return () => {
      isMounted = false;
    };
  }, [open]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error for this field
    if (formErrors[name]) {
      setFormErrors({
        ...formErrors,
        [name]: null,
      });
    }
  };

  const handleSwitchChange = (e) => {
    if (e.target.checked && !formData.personnelId) {
      setError("Chưa gán nhân sự nên không kích hoạt được tài khoản.");
      setFormData({
        ...formData,
        isActive: false,
      });
      return;
    }

    setFormData({
      ...formData,
      [e.target.name]: e.target.checked,
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.userName || formData.userName.trim() === "") {
      errors.userName = "Tên đăng nhập không được để trống";
    }

    if (!formData.password || formData.password.length < 6) {
      errors.password = "Mật khẩu phải có ít nhất 6 ký tự";
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = "Mật khẩu xác nhận không khớp";
    }

    if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = "Email không hợp lệ";
    }

    return errors;
  };

  const handleSubmit = async () => {
    // Validate form
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await registerUser({
        userName: formData.userName,
        password: formData.password,
        confirmPassword: formData.confirmPassword, // Thêm trường confirmPassword
        fullName: formData.fullName || null,
        email: formData.email || null,
        personnelId: formData.personnelId || null,
        isActive: Boolean(formData.personnelId) && formData.isActive,
      });

      setSuccess(true);

      // Reset form after successful submission
      setTimeout(() => {
        setFormData({
          userName: "",
          password: "",
          confirmPassword: "",
          fullName: "",
          email: "",
          personnelId: null,
          isActive: false,
        });
        setSuccess(false);
        onUserAdded();
        onClose();
      }, 1500);
    } catch (err) {
      console.error("Error registering user:", err);
      if (err.response && err.response.data) {
        setError(
          err.response.data.message || "Có lỗi xảy ra khi tạo người dùng."
        );
      } else {
        setError("Có lỗi xảy ra khi tạo người dùng. Vui lòng thử lại sau.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Reset form and errors on close
    setFormData({
      userName: "",
      password: "",
      confirmPassword: "",
      fullName: "",
      email: "",
      personnelId: null,
      isActive: false,
    });
    setFormErrors({});
    setError(null);
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Thêm người dùng mới</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" className="alert-margin">
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" className="alert-margin">
            Tạo người dùng thành công!
          </Alert>
        )}

        <div className="form-row">
          <TextField
            name="userName"
            label="Tên đăng nhập *"
            value={formData.userName}
            onChange={handleChange}
            fullWidth
            error={!!formErrors.userName}
            helperText={formErrors.userName}
            disabled={isSubmitting || success}
            margin="normal"
          />
        </div>

        <div className="form-row form-row-2-col">
          <TextField
            name="password"
            label="Mật khẩu *"
            type="password"
            value={formData.password}
            onChange={handleChange}
            error={!!formErrors.password}
            helperText={formErrors.password}
            disabled={isSubmitting || success}
            margin="normal"
          />

          <TextField
            name="confirmPassword"
            label="Xác nhận mật khẩu *"
            type="password"
            value={formData.confirmPassword}
            onChange={handleChange}
            error={!!formErrors.confirmPassword}
            helperText={formErrors.confirmPassword}
            disabled={isSubmitting || success}
            margin="normal"
          />
        </div>

        <div className="form-row">
          <Autocomplete
            options={personnelOptions}
            value={
              personnelOptions.find(
                (item) => item.personnelId === formData.personnelId
              ) || null
            }
            getOptionLabel={(option) =>
              `${option.fullName || option.personnelName || "Nhân sự"}${
                option.personnelCode ? ` (${option.personnelCode})` : ""
              }`
            }
            onChange={(event, value) => {
              setError(null);
              setFormData((prev) => ({
                ...prev,
                personnelId: value?.personnelId || null,
                fullName: value?.fullName || "",
                email: value?.email || "",
                isActive: value ? prev.isActive : false,
              }));
            }}
            disabled={isSubmitting || success}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Nhân sự liên kết"
                margin="normal"
                helperText={
                  !formData.personnelId
                    ? "Chưa chọn nhân sự thì tài khoản không thể kích hoạt."
                    : ""
                }
              />
            )}
          />
        </div>

        <div className="form-row">
          <TextField
            name="fullName"
            label="Tên người dùng"
            value={formData.fullName}
            onChange={handleChange}
            fullWidth
            disabled={isSubmitting || success}
            margin="normal"
            helperText="Tự động lấy theo nhân sự liên kết, có thể chỉnh sửa"
          />
        </div>

        <div className="form-row">
          <TextField
            name="email"
            label="Email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            fullWidth
            error={!!formErrors.email}
            helperText={formErrors.email}
            disabled={isSubmitting || success}
            margin="normal"
          />
        </div>

        <div className="form-row">
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive}
                onChange={handleSwitchChange}
                name="isActive"
                color="primary"
                disabled={isSubmitting || success || !formData.personnelId}
              />
            }
            label="Kích hoạt tài khoản"
          />
        </div>
      </DialogContent>
      <DialogActions>
        <AppButton variant="outlined" onClick={handleClose} disabled={isSubmitting}>
          Hủy
        </AppButton>
        <AppButton
          onClick={handleSubmit}
          variant="contained"
          disabled={isSubmitting || success}
        >
          {isSubmitting ? <CircularProgress size={24} /> : "Tạo người dùng"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default AddUserModal;
